import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import { DateCell, TextCell } from "@tfg/ui"
import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"

import {
  FindIngestionsResponseDto,
  FindIngestionsResponseDtoTypeEnumKey,
  findIngestionsResponseDtoTypeEnum,
} from "~/gen/api"
import { IngestionStatusBadge } from "~/pages/Admin/Ingestion/ProductImport/components/IngestionStatusBadge"
import { IngestionActionsMenu } from "~/pages/Brand/DataTransfer/Import/components/IngestionActionsMenu"

const columnHelper = createColumnHelper<FindIngestionsResponseDto>()
export const useColumns = ({
  handleErrorModalOpen,
}: {
  handleErrorModalOpen: (ingestionId: string) => void
}) => {
  const { t } = useTranslation("dataTransfer")

  const typeLabel = useCallback(
    (type: FindIngestionsResponseDtoTypeEnumKey) => {
      const resolver = {
        [findIngestionsResponseDtoTypeEnum.PRODUCT]: t(
          "import.legacy.templateCard.title"
        ),
        [findIngestionsResponseDtoTypeEnum.BUSINESS_PARTNER]: t(
          "import.businessPartner.templateCard.title"
        ),
      }
      return resolver[type]
    },
    [t]
  )

  return useMemo<ColumnDef<FindIngestionsResponseDto, any>[]>(
    () => [
      columnHelper.accessor("type", {
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("import.list.table.columns.type.header")}
            tooltip={t("import.list.table.columns.type.tooltip")}
          />
        ),
        cell: (info) => {
          const value = info.getValue()
          return (
            <TextCell
              value={typeLabel(value)}
              tooltip={t("import.list.table.columns.type.tooltip")}
            />
          )
        },
      }),
      columnHelper.accessor("document.originalFileName", {
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("import.list.table.columns.file.header")}
            tooltip={t("import.list.table.columns.file.tooltip")}
          />
        ),
        cell: (info) => {
          const value = info.getValue()
          return <TextCell value={value} />
        },
      }),
      columnHelper.accessor("product_count", {
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("import.list.table.columns.rows.header")}
            tooltip={t("import.list.table.columns.rows.tooltip")}
          />
        ),
        cell: (info) => {
          const value = info.getValue()
          return (
            <TextCell
              value={value}
              tooltip={t("import.list.table.columns.rows.tooltip")}
            />
          )
        },
      }),
      columnHelper.accessor("createdBy.email", {
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("import.list.table.columns.user.header")}
            tooltip={t("import.list.table.columns.user.tooltip")}
          />
        ),
        cell: (info) => {
          const value = info.getValue()
          return <TextCell value={value} />
        },
      }),
      columnHelper.accessor("createdAt", {
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("import.list.table.columns.date.header")}
            tooltip={t("import.list.table.columns.date.tooltip")}
          />
        ),
        cell: (info) => <DateCell date={info.getValue()} format="l HH:mm" />,
      }),
      columnHelper.accessor("status", {
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("import.list.table.columns.status.header")}
            tooltip={t("import.list.table.columns.status.tooltip")}
          />
        ),
        cell: (info) => {
          return <IngestionStatusBadge status={info.getValue()} />
        },
      }),
      columnHelper.display({
        id: "action-cell-launch",
        cell: ({ row }) => {
          return (
            <IngestionActionsMenu
              row={row}
              handleErrorModalOpen={handleErrorModalOpen}
            />
          )
        },
      }),
    ],
    [t, handleErrorModalOpen, typeLabel]
  )
}
